import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Table from "../../../core/common/dataTable/index";
import { AcademicYearTable } from "../../../core/data/interface"; //Les types des colones de notre tableau déclaré au préalable
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AxiosError } from "axios";
import {
  deleteAcademicYear,
  getAllAcademicYear,
} from "../../../api/generalParam";
import EditAcademicYearModal from "./editAcademicYearModal";
import CreateAcademicYearModal from "./createAcademicYear";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

// bouton protégée pour créer une année universitaire
const ProtectedBtnAddAcademicYear = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => {
          props.setShowCreate(true);
        }}
        //   to="#"
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-square-rounded-plus me-2" />
        Nouvelle Année
      </button>
    </div>
  ),
  [buildPermission(Resource.ACADEMIC_YEAR, Action.CREATE)],
  "masquer",
);

// bouton protégée pour modifier une année universitaire
const ProtectedBtnUpdateAcademicYear = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setEditYear(props.record.id);
          props.setShowEdit(true);
        }}
      >
        <i className="ti ti-edit-circle me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.ACADEMIC_YEAR, Action.UPDATE)],
  "masquer",
);

// bouton protégée pour supprimer une année universitaire
const ProtectedBtnDeleteAcademicYear = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.ACADEMIC_YEAR, Action.DELETE)],
  "masquer",
);

const AcademicYearList = () => {
  //Initialisation des state utilisés
  const [allYears, setAllYears] = useState([]);
  const [editYear, setEditYear] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [refreshKey, setRefreshKey] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  // Les colonnes de notre tableau
  const columns = [
    {
      title: "Libellé",
      dataIndex: "label",
      render: (text: string, record: any) => `Année ${record.label}`,
      sorter: (a: AcademicYearTable, b: AcademicYearTable) =>
        a.label.length - b.label.length,
    },
    {
      title: "Date de début",
      dataIndex: "startDate",
      sorter: (a: AcademicYearTable, b: AcademicYearTable) =>
        a.startDate.length - b.startDate.length,
    },
    {
      title: "Date de Fin",
      dataIndex: "endDate",
      sorter: (a: AcademicYearTable, b: AcademicYearTable) =>
        a.endDate.length - b.endDate.length,
    },
    {
      title: "Statut",
      dataIndex: "statut",
      render: (text: boolean) => (
        <>
          {text === true ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              Actif
            </span>
          ) : (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              Inactif
            </span>
          )}
        </>
      ),
      sorter: (a: AcademicYearTable, b: AcademicYearTable) =>
        a.statut === b.statut ? 0 : a.statut ? -1 : 1,
    },
    {
      title: "Date de création",
      dataIndex: "createdAt",
      render: (date: string) => {
        const d = new Date(date);
        return d.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      sorter: (a: AcademicYearTable, b: AcademicYearTable) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <ProtectedBtnUpdateAcademicYear
                  setEditYear={setEditYear}
                  record={record}
                  setShowEdit={setShowEdit}
                />

                <ProtectedBtnDeleteAcademicYear
                  showConfirmationAlert={showConfirmationAlert}
                  record={record}
                />
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  //Traitement en alert de la demande de suppression d'une année académique
  const showConfirmationAlert = (academicYear: any) => {
    MySwal.fire({
      title: "Supprimer une année académique ?",
      text: `Souhaitez-vous vraiment supprimer l'année académique ${academicYear.label} ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Supprimer !",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête de suppression
          await deleteAcademicYear(academicYear.id);
          MySwal.fire({
            title: "Année Académique supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          // Relancer le useEffect
          reloadData();
        } catch (error) {
          const err = error as AxiosError<any>;
          // console.log(err);

          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  //Reload de la liste
  const reloadData = () => setRefreshKey((prev) => prev + 1);

  //On recupère la liste des Years en appelant sa fonction
  useEffect(() => {
    setHasLoaded(false);
    setIsLoading(true);

    getAllAcademicYear()
      .then((years) => {
        setAllYears(years.data);
      })
      .finally(() => {
        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      });
  }, [refreshKey]);

  return (
    <>
      {/* Page Header */}
      <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
        {/* <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                Gestion des Années Académiques
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="#">Paramètres</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Années Académiques
                  </li>
                </ol>
              </nav>
            </div> */}
        {/* Boutton en entête de la page */}
        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
          {/* <TooltipOption/> */}
          <ProtectedBtnAddAcademicYear setShowCreate={setShowCreate} />
        </div>
      </div>
      {/* /Page Header */}

      {/* Tableau liste du personnel administratif */}
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
          <h4 className="mb-3">Liste des Années Académiques</h4>
        </div>
        <div className="card-body p-0 py-3">
          {/* Staffs List (On affiche le tableau qui si les données son chargées même vide) */}
          {isLoading && !hasLoaded ? (
            <div className="text-center">
              <div className="spinner-border" role="status"></div>
              <span className="visually">Chargement des Données...</span>
            </div>
          ) : (
            <Table columns={columns} dataSource={allYears} Selection={true} />
          )}
          {/* /Staffs List */}
        </div>
      </div>
      {/* /Page Header */}

      <EditAcademicYearModal
        show={showEdit}
        yearID={editYear}
        onClose={() => setShowEdit(false)}
        onSaved={reloadData}
      />
      <CreateAcademicYearModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onSaved={reloadData}
      />
    </>
  );
};

// export default withPermission(AcademicYearList, "admin:access", "accès-refuser");
export default AcademicYearList;
